/** @file quadVertices: one impostor's CPU corners as four Babylon vertices plus raster's per-quad constants. */
import { corners } from '@zilo/pose3d-render-core/corners/corners';
import type { ImpostorList } from '@zilo/pose3d-render-core/contract';
import { quadDepth } from '@zilo/pose3d-render-core/depth/quadDepth';
import { IMPOSTOR_LAYOUT as L } from '@zilo/pose3d-render-core/layout/impostorLayout';
import { displayOf } from '@zilo/pose3d-runtime-babylon/camera/displayOf';
import { unprojectScreen } from '@zilo/pose3d-runtime-babylon/camera/unprojectScreen';
import type { QuadBuffers, QuadFrame } from '@zilo/pose3d-runtime-babylon/draw/quadFrame';

/**
 * QUAD_MARGIN_PX: how far (perpendicular, in target pixels) the drawn quad is grown beyond the tile. GPUs snap vertices
 * to a sub-pixel grid (SwiftShader: 1/16 px) and apply their own tie rule; the grown quad only has to produce a
 * fragment for every pixel centre raster could cover, the fragment shader then decides as raster does.
 */
const QUAD_MARGIN_PX = 1;

/** Quad parameters `(s, t)` of the corners `[tl, tr, br, bl]` (raster's parallelogram `tl + s A + t B`). */
const ST = [0, 0, 1, 0, 1, 1, 0, 1] as const;

/** The grown parameter range: `[-ds, 1 + ds] x [-dt, 1 + dt]`, or null for a degenerate quad (raster draws nothing). */
function margins(a: [number, number], b: [number, number]): [number, number] | null {
  const area = Math.abs(a[0] * b[1] - a[1] * b[0]);
  if (!(area >= 1e-9)) return null;
  return [(QUAD_MARGIN_PX * Math.hypot(b[0], b[1])) / area, (QUAD_MARGIN_PX * Math.hypot(a[0], a[1])) / area];
}

/** Writes the per-quad constants of slot `slot` on its four vertices. */
function writeConstants(out: QuadBuffers, slot: number, consts: { geomA: number[]; geomB: number[]; depth3: number[]; cut3: number[]; tex: number[] }): void {
  for (let k = 0; k < 4; k++) {
    const v = slot * 4 + k;
    out.geomA.set(consts.geomA, v * 4);
    out.geomB.set(consts.geomB, v * 4);
    out.depth3.set(consts.depth3, v * 3);
    out.cut3.set(consts.cut3, v * 3);
    out.texRect.set(consts.tex, v * 4);
  }
}

/** The four positions of slot `slot`: the parallelogram `tl + s A + t B` grown to `margins`, unprojected (Babylon axes). */
function writeGrown(out: QuadBuffers, slot: number, q: { tl: [number, number]; a: [number, number]; b: [number, number]; d: number[] }, frame: QuadFrame): void {
  const { view } = frame;
  const grow = margins(q.a, q.b);
  if (grow === null) { out.positions.fill(0, slot * 12, slot * 12 + 12); return; }
  const centre = displayOf(view.target, view.zScaleView);
  const [d0, d1, d3] = [q.d[0] ?? 0, q.d[1] ?? 0, q.d[2] ?? 0];
  for (let k = 0; k < 4; k++) {
    const s = ST[k * 2] === 0 ? -grow[0] : 1 + grow[0];
    const t = ST[k * 2 + 1] === 0 ? -grow[1] : 1 + grow[1];
    const off = unprojectScreen(q.tl[0] + s * q.a[0] + t * q.b[0] - view.originX, q.tl[1] + s * q.a[1] + t * q.b[1] - view.originY,
      d0 + s * (d1 - d0) + t * (d3 - d0), view.basis, view.pixelsPerUnit);
    out.positions.set([centre[0] + off[0], centre[2] + off[2], centre[1] + off[1]], slot * 12 + k * 3);
  }
}

/**
 * Writes list entry `index` at quad slot `slot` from `c = corners(list, index, camera, profile)` (stretch and cut
 * applied): the constants raster fills with (tl, A, B, flips, depths and cut distances at tl/tr/bl, tex), where depth
 * = `c.depths` (`slope`) or `quadDepth` (`flat`); and four positions = the corners grown by `QUAD_MARGIN_PX`, each
 * `displayOf(view.target) + unprojectScreen(corner - origin, depth there)` in Babylon axes (`(x, y, z) -> (x, z, y)`).
 * A degenerate quad gets zero positions (no fragment). `sheetSize` is not needed by the texel-exact shader.
 */
export function quadVertices(list: ImpostorList, index: number, frame: QuadFrame, sheetSize: [number, number], out: QuadBuffers, slot: number): void {
  const { view } = frame;
  const c = corners(list, index, frame.camera, frame.profile);
  const [tl, tr, , bl] = c.corners;
  const a: [number, number] = [tr[0] - tl[0], tr[1] - tl[1]];
  const b: [number, number] = [bl[0] - tl[0], bl[1] - tl[1]];
  const d = frame.depthMode === 'flat' ? new Array<number>(4).fill(quadDepth(list, index, view)) : c.depths;
  const [d0, d1, d3] = [d[0] ?? 0, d[1] ?? 0, d[3] ?? 0];
  const flags = list[index * L.stride + L.flags] ?? 0;
  writeConstants(out, slot, {
    geomA: [tl[0], tl[1], a[0], a[1]], geomB: [b[0], b[1], (flags & 1) !== 0 ? 1 : 0, (flags & 2) !== 0 ? 1 : 0],
    depth3: [d0, d1, d3], cut3: [c.cutDist[0], c.cutDist[1], c.cutDist[3]], tex: c.tex,
  });
  writeGrown(out, slot, { tl, a, b, d: [d0, d1, d3] }, frame);
}
